import { ParcsOrganizations } from './constants-enums/lanai-enums';
import { IParcsGetLotConfigurationResponse } from './interfaces/parcs-interfaces';

/**
 * The PARCS vendor a lot is wired to. Use this instead of comparing raw parcsId GUIDs.
 *
 * None    - the lot has no PARCS configuration at all.
 * Unknown - the lot has a PARCS configuration, but the parcsId is not one we recognize.
 *           Treat this as "has a PARCS, but do not run vendor specific logic".
 */
export enum ParcsType {
  None = 'None',
  Unknown = 'Unknown',
  AmanoOne = 'AmanoOne',
  Flash = 'Flash',
}

/** parcsId (upper case GUID) -> vendor. Add new vendors here and nowhere else. */
const PARCS_TYPE_BY_ID: Readonly<Record<string, ParcsType>> = {
  [ParcsOrganizations.AMANO_ONE_PARCS_ID]: ParcsType.AmanoOne,
  [ParcsOrganizations.FLASH_PARCS_ID]: ParcsType.Flash,
};

export class ParcsTypeHelper {
  /** Maps a raw parcsId to its vendor. Case and whitespace insensitive. */
  public static fromParcsId(parcsId: string | null | undefined): ParcsType {
    const normalizedParcsId = (parcsId ?? '').trim().toUpperCase();

    if (normalizedParcsId === '') {
      return ParcsType.None;
    }

    return PARCS_TYPE_BY_ID[normalizedParcsId] ?? ParcsType.Unknown;
  }

  /**
   * Maps a lot's PARCS configuration to its vendor.
   * The config is an empty object ({} as IParcsGetLotConfigurationResponse) when the lot has no PARCS,
   * so an absent parcsId resolves to ParcsType.None.
   */
  public static fromLotConfiguration(
    lotParcsConfiguration: IParcsGetLotConfigurationResponse | null | undefined,
  ): ParcsType {
    return ParcsTypeHelper.fromParcsId(lotParcsConfiguration?.parcsId);
  }

  public static isAmanoOne(source: ParcsType | IParcsGetLotConfigurationResponse | string | null | undefined): boolean {
    return ParcsTypeHelper.resolve(source) === ParcsType.AmanoOne;
  }

  public static isFlash(source: ParcsType | IParcsGetLotConfigurationResponse | string | null | undefined): boolean {
    return ParcsTypeHelper.resolve(source) === ParcsType.Flash;
  }

  /** True when the lot is linked to any PARCS, recognized or not. */
  public static hasParcs(source: ParcsType | IParcsGetLotConfigurationResponse | string | null | undefined): boolean {
    return ParcsTypeHelper.resolve(source) !== ParcsType.None;
  }

  private static resolve(
    source: ParcsType | IParcsGetLotConfigurationResponse | string | null | undefined,
  ): ParcsType {
    if (source == null) {
      return ParcsType.None;
    }

    if (typeof source === 'string') {
      return Object.values(ParcsType).includes(source as ParcsType)
        ? (source as ParcsType)
        : ParcsTypeHelper.fromParcsId(source);
    }

    return ParcsTypeHelper.fromLotConfiguration(source);
  }
}
